import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type {
  DayOfWeekBucketShape,
  HourOfDayBucketShape,
} from "../../types/discount-analytics-types";

interface Props {
  dayOfWeek: DayOfWeekBucketShape[];
  hourOfDay: HourOfDayBucketShape[];
}

const HOUR_BUCKETS: HourOfDayBucketShape["bucket"][] = ["MORNING", "AFTERNOON", "EVENING", "LATE"];

const DAY_LABEL_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

function intensityColor(value: number, max: number): string {
  if (max === 0 || value === 0) return "bg-slate-100";
  const ratio = value / max;
  if (ratio < 0.2) return "bg-amber-100";
  if (ratio < 0.4) return "bg-amber-200";
  if (ratio < 0.6) return "bg-amber-300";
  if (ratio < 0.8) return "bg-amber-400";
  return "bg-amber-500";
}

export function TimingHeatmap({ dayOfWeek, hourOfDay }: Props) {
  const { t } = useTranslation("reports");

  const totalRedemptions = dayOfWeek.reduce((sum, b) => sum + b.redemptionCount, 0);
  const maxDayCount = Math.max(1, ...dayOfWeek.map((b) => b.redemptionCount));
  const maxHourCount = Math.max(1, ...hourOfDay.map((b) => b.redemptionCount));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-500" />
          {t("discounts.timing.title")}
        </CardTitle>
        <CardDescription>{t("discounts.timing.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {totalRedemptions === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            {t("discounts.timing.emptyState")}
          </div>
        ) : (
          <>
            <section>
              <div className="text-xs text-muted-foreground mb-2">
                {t("discounts.timing.byDay")}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {dayOfWeek.map((bucket, idx) => {
                  const shortLabel = t(`discounts.timing.day.${DAY_LABEL_KEYS[idx]}`);
                  const longLabel = t(`discounts.timing.day.${DAY_LABEL_KEYS[idx]}Long`);
                  return (
                    <TooltipProvider key={bucket.dayOfWeek} delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "rounded-md p-2 text-center cursor-default transition-colors",
                              intensityColor(bucket.redemptionCount, maxDayCount)
                            )}
                          >
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-900">
                              <span className="sm:hidden">{shortLabel}</span>
                              <span className="hidden sm:inline">{longLabel}</span>
                            </div>
                            <div className="text-sm font-semibold tabular-nums text-slate-900">
                              {bucket.redemptionCount}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          <div>
                            <strong>{longLabel}</strong>
                          </div>
                          <div>
                            {t("discounts.timing.tooltipCount", {
                              count: bucket.redemptionCount,
                            })}
                          </div>
                          <div>
                            <PriceDisplay amount={bucket.totalAmountDiscounted} />
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </section>

            <section>
              <div className="text-xs text-muted-foreground mb-2">
                {t("discounts.timing.byHour")}
              </div>
              <div className="grid grid-cols-4 gap-1">
                {HOUR_BUCKETS.map((bucketKey) => {
                  const bucket = hourOfDay.find((b) => b.bucket === bucketKey) ?? {
                    bucket: bucketKey,
                    redemptionCount: 0,
                    totalAmountDiscounted: 0,
                  };
                  const label = t(`discounts.timing.hour.${bucketKey}`);
                  const range = t(`discounts.timing.hourRange.${bucketKey}`);
                  return (
                    <TooltipProvider key={bucketKey} delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "rounded-md p-3 text-center cursor-default transition-colors",
                              intensityColor(bucket.redemptionCount, maxHourCount)
                            )}
                          >
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-900">
                              {label}
                            </div>
                            <div className="text-[9px] font-semibold text-slate-800">{range}</div>
                            <div className="mt-1 text-lg font-semibold tabular-nums text-slate-900">
                              {bucket.redemptionCount}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          <div>
                            <strong>
                              {label} ({range})
                            </strong>
                          </div>
                          <div>
                            {t("discounts.timing.tooltipCount", {
                              count: bucket.redemptionCount,
                            })}
                          </div>
                          <div>
                            <PriceDisplay amount={bucket.totalAmountDiscounted} />
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
}
